/**
 * Live Operations Center — NOC industrial en tiempo real.
 *
 * Auto-refresca cada 4s (simulando streaming/WebSocket batching en
 * modo demo) y muestra: métricas vivas, activity feed de eventos
 * event-sourced, salud de infraestructura y anomalías emergentes.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import type { LiveEvent } from "../api/mock";
import type { FiltroRealtime } from "../api/contracts";
import { Icon, type IconName } from "../components/Icon";
import { Ticker } from "../components/Ticker";
import { PageHeader } from "../layout/PageHeader";
import { useLiveDemo } from "../system/useLiveDemo";

/**
 * Filtro de la Suscripcion_Realtime del centro de mando: refleja en vivo
 * Eventos_Operacionales y cambios de Alerta sobre toda la operación
 * (R1.5, R7.4, R7.5).
 */
const FILTRO_LIVE_OPS: FiltroRealtime = {
  tipos: [
    "alerta_generada",
    "alerta_cambio_estado",
    "anomalia_detectada",
    "caso_creado",
    "caso_actualizado",
    "valvula_abierta",
    "valvula_cerrada",
    "orden_creada",
  ],
};

interface LiveSnapshot {
  events: LiveEvent[];
  _source?: string;
  metrics: {
    ingest_rate_h: number;
    pipeline_lag_s: number;
    alertas_activas: number;
    gateways_online: number;
    gateways_total: number;
    cuadrillas_activas: number;
    sla_en_riesgo: number;
    api_p95_ms: number;
    presion_media_bar: number;
  };
}

const EVENT_ICON: Record<LiveEvent["tipo"], IconName> = {
  alerta_creada: "bell",
  orden_asignada: "wrench",
  inspeccion_iniciada: "search",
  fuga_confirmada: "droplet",
  evidencia_subida: "shield",
  sla_vencido: "clock",
  gateway_offline: "wifi",
  dma_degradado: "droplet",
  medidor_reemplazado: "settings",
  lectura_batch: "server",
};

function eventTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function LiveOperations(): JSX.Element {
  // Siembra el Event_Store y arranca el stream en vivo de la Capa para
  // alimentar el Ticker_Operacional (R7.4); difiere la suscripción hasta
  // que el backlog esté listo.
  const { listo } = useLiveDemo();

  const liveQuery = useQuery<LiveSnapshot>({
    queryKey: ["live"],
    queryFn: () => api<LiveSnapshot>("/live"),
    refetchInterval: 4000,
    staleTime: 0,
  });

  const snap = liveQuery.data;
  const m = snap?.metrics;

  return (
    <>
      <PageHeader
        eyebrow="Centro de mando"
        title="Live Operations"
        subtitle="Centro de control en tiempo real: eventos, ingestión, infraestructura y SLA."
        actions={
          <span className="badge badge--accent">
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "var(--c-success)",
                display: "inline-block",
                marginRight: 6,
                animation: "fade-in 1s ease-in-out infinite alternate",
              }}
            />
            {snap?._source === "rf-api" ? "EN VIVO · rf-api" : "LIVE · auto-refresh 4s"}
          </span>
        }
      />

      {m && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className="kpi kpi--accent">
              <span className="kpi__label">Ingestión</span>
              <span className="kpi__value">{(m.ingest_rate_h / 1000).toFixed(0)}k/h</span>
              <span className="kpi__hint">lag {m.pipeline_lag_s}s</span>
            </div>
            <div className="kpi kpi--danger">
              <span className="kpi__label">Alertas activas</span>
              <span className="kpi__value">{m.alertas_activas}</span>
              <span className="kpi__hint">{m.sla_en_riesgo} SLA en riesgo</span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">Gateways online</span>
              <span className="kpi__value">
                {m.gateways_online}/{m.gateways_total}
              </span>
              <span className="kpi__hint">red LoRaWAN</span>
            </div>
            <div className="kpi kpi--warn">
              <span className="kpi__label">Cuadrillas activas</span>
              <span className="kpi__value">{m.cuadrillas_activas}</span>
              <span className="kpi__hint">en ruta / en sitio</span>
            </div>
            <div className="kpi kpi--magenta">
              <span className="kpi__label">API p95</span>
              <span className="kpi__value">{m.api_p95_ms} ms</span>
              <span className="kpi__hint">gateway de API</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Presión media red</span>
              <span className="kpi__value">{m.presion_media_bar} bar</span>
              <span className="kpi__hint">telemetría SCADA</span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 12 }}>
            <section className="card">
              <div className="card__title">
                <span>Activity feed · event stream</span>
                <span className="text-faint text-mono" style={{ fontSize: 11 }}>
                  append-only
                </span>
              </div>
              <div className="vstack" style={{ gap: 0 }}>
                {snap?.events.map((ev) => (
                  <div
                    key={ev.event_id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: 10,
                      alignItems: "center",
                      padding: "8px 4px",
                      borderBottom: "1px solid var(--c-divider)",
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 7,
                        display: "grid",
                        placeItems: "center",
                        background:
                          ev.severidad === "critica"
                            ? "var(--c-danger-soft)"
                            : ev.severidad === "alta"
                              ? "var(--c-warn-soft)"
                              : ev.severidad === "media"
                                ? "var(--c-info-soft)"
                                : "var(--c-surface-3)",
                        color:
                          ev.severidad === "critica"
                            ? "var(--c-danger)"
                            : ev.severidad === "alta"
                              ? "var(--c-warn)"
                              : ev.severidad === "media"
                                ? "var(--c-info)"
                                : "var(--c-text-muted)",
                      }}
                    >
                      <Icon name={EVENT_ICON[ev.tipo] ?? "bell"} size={13} />
                    </span>
                    <div>
                      <div style={{ fontSize: 12.5, color: "var(--c-text)" }}>{ev.mensaje}</div>
                      <div className="text-faint" style={{ fontSize: 10.5 }}>
                        {ev.tipo.replace(/_/g, " ")}
                        {ev.ref ? ` · ${ev.ref}` : ""}
                      </div>
                    </div>
                    <div className="hstack" style={{ gap: 8 }}>
                      <span className="text-mono text-faint" style={{ fontSize: 11 }}>
                        {eventTime(ev.ts)}
                      </span>
                      {ev.to && (
                        <Link to={ev.to} className="btn btn--ghost btn--icon" aria-label="Abrir">
                          <Icon name="chevron-right" size={12} />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="card" style={{ alignSelf: "start" }}>
              <div className="card__title">Salud de infraestructura</div>
              <HealthRow label="IoT Core (LoRaWAN)" estado="healthy" detail="ingesta nominal" />
              <HealthRow label="Kafka / MSK" estado="healthy" detail={`lag ${m.pipeline_lag_s}s`} />
              <HealthRow label="Pipeline anomaly-engine" estado="degraded" detail="p95 1.3s" />
              <HealthRow label="Pipeline scoring" estado="healthy" detail="p95 260ms" />
              <HealthRow label="API Gateway" estado="healthy" detail={`p95 ${m.api_p95_ms}ms`} />
              <HealthRow label="Gateway GW-DMA-005" estado="down" detail="offline 6 min" />

              <div className="card__title" style={{ marginTop: 16 }}>Anomalías emergentes</div>
              <Link to="/insights" className="status-block status-block--alert" style={{ textDecoration: "none" }}>
                <Icon name="bell" size={14} />
                <span>DMA-003 · MNF +18% en 72h → fuga probable</span>
              </Link>
              <Link
                to="/observabilidad"
                className="status-block"
                style={{ textDecoration: "none", marginTop: 8 }}
              >
                <Icon name="wifi" size={14} />
                <span>Cobertura RF norte degradada (3 gateways)</span>
              </Link>
            </section>
          </div>

          {/* Ticker_Operacional: flujo en vivo de Eventos_Operacionales y
              cambios de Alerta vía Suscripcion_Realtime (R1.5, R7.4, R7.5).
              Marca "estados posiblemente obsoletos" al interrumpirse (R1.9). */}
          <div style={{ marginTop: 12 }}>
            {listo ? (
              <Ticker
                filtro={FILTRO_LIVE_OPS}
                titulo="Ticker operacional · stream en vivo"
                maxVisibles={40}
              />
            ) : (
              <section className="card">
                <p className="text-faint" style={{ fontSize: 12.5, margin: "8px 0" }}>
                  Conectando al stream operacional…
                </p>
              </section>
            )}
          </div>
        </>
      )}
    </>
  );
}

function HealthRow({
  label,
  estado,
  detail,
}: {
  label: string;
  estado: "healthy" | "degraded" | "down";
  detail: string;
}): JSX.Element {
  const tone = estado === "healthy" ? "accent" : estado === "degraded" ? "warn" : "danger";
  return (
    <div className="alert-component">
      <span className="hstack" style={{ gap: 8 }}>
        <span
          className="map-legend__dot"
          style={{
            background:
              estado === "healthy"
                ? "var(--c-success)"
                : estado === "degraded"
                  ? "var(--c-warn)"
                  : "var(--c-danger)",
          }}
        />
        {label}
      </span>
      <strong>
        <span className={`badge badge--${tone}`}>{detail}</span>
      </strong>
    </div>
  );
}
